import type { ComponentPropsWithoutRef, ReactElement } from 'react'
import { Spinner } from './Spinner.tsx'

/**
 * The only button in the app.
 *
 * Four variants, and the list is closed on purpose: before this, buttons
 * were 824 inline `className` literals and every screen had invented its
 * own blue. If a screen needs a fifth look, the answer is almost always
 * that it wants one of these four and is wrong about it.
 *
 * No `className` escape hatch. The point of the token layer is that a
 * reviewer can restyle the app from `styles.css`; a pass-through
 * `className` is how `bg-gray-50` gets back in.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost'
export type ButtonSize = 'sm' | 'md'

/** `disabled` is replaced by `isDisabled` (house naming), and `className`
 * is removed rather than forwarded — see above. */
export interface ButtonProps extends Omit<
  ComponentPropsWithoutRef<'button'>,
  'disabled' | 'className'
> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Swaps the leading slot for a spinner and blocks the click. The label
   * stays put: a button that changes width mid-request moves the thing the
   * operator is about to click next. */
  isLoading?: boolean
  isDisabled?: boolean
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  // The accent fill. `text-accent-fg` is the ONLY text colour legal on it —
  // in the dark theme the accent is a bright fill with dark text, which is
  // the single arrangement that lets one accent hue serve both a button
  // fill and a link colour on a near-black canvas.
  primary:
    'border-accent bg-accent text-accent-fg hover:bg-accent-hover hover:border-accent-hover',
  secondary: 'border-border bg-surface text-fg hover:bg-elevated',
  // There is no `--color-status-danger-hover`, and inventing one would mean
  // a seventh colour to hold at 4.5:1 in two themes. Hover brightens the
  // EDGE to the status foreground instead — same family, already verified,
  // and on a destructive control a sharpening outline reads better than a
  // fill that shifts under the cursor.
  danger:
    'border-status-danger-border bg-status-danger text-status-danger-fg hover:border-status-danger-fg',
  ghost:
    'border-transparent bg-transparent text-muted hover:bg-elevated hover:text-fg',
}

const SIZE_CLASSES: Record<ButtonSize, string> = {
  // Height matches `--spacing-row` at `md` so a button sitting in a table
  // row does not make that row taller than the 32px the density depends on.
  sm: 'h-6 gap-1 px-2 text-xs',
  md: 'h-row gap-1.5 px-3 text-sm',
}

const BASE_CLASSES =
  'inline-flex items-center justify-center rounded-md border font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50'

export function Button({
  variant = 'secondary',
  size = 'md',
  isLoading = false,
  isDisabled = false,
  type = 'button',
  children,
  ...rest
}: ButtonProps): ReactElement {
  return (
    <button
      // `type` defaults to `button`, not `submit`: a bare <button> inside a
      // form submits it, which is how "Remove row" buttons have historically
      // saved the whole form by accident.
      type={type}
      disabled={isDisabled || isLoading}
      // `aria-busy` rather than swapping the label: a screen reader user
      // hears the same button, now reported busy, instead of the control
      // vanishing and a "Loading" appearing somewhere else.
      aria-busy={isLoading || undefined}
      className={`${BASE_CLASSES} ${SIZE_CLASSES[size]} ${VARIANT_CLASSES[variant]}`}
      {...rest}
    >
      {isLoading && <Spinner size="sm" label="Working" />}
      {children}
    </button>
  )
}
